import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Shield,
  Gavel,
  AlertTriangle,
  Calendar,
  FileText,
  Building2,
  ArrowRight,
  LayoutDashboard,
  Pencil,
  User,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LegalSummary {
  expiringOrdersCount: number;
  upcomingHearingsCount: number;
  openCasesCount: number;
  recentActivity: RecentActivity[];
}

interface RecentActivity {
  id: number;
  person_id: number;
  hearing_date: string | null;
  hearing_type: string | null;
  disposition: string | null;
  court_name?: string;
  judge_name?: string;
  staff_first_name?: string;
  staff_last_name?: string;
  created_at: string;
}

interface ExpiringOrder {
  id: number;
  person_id: number;
  order_type: string | null;
  expiration_date: string | null;
  status: string | null;
  client_first_name?: string | null;
  client_last_name?: string | null;
  client_internal_id?: string | null;
}

interface AllLegalCase {
  id: number;
  person_id: number;
  case_details: string | null;
  law_enforcement: string | null;
  created_at: string;
  client_first_name?: string | null;
  client_last_name?: string | null;
  client_internal_id?: string | null;
  author_first_name?: string | null;
  author_last_name?: string | null;
}

interface AllProtectionOrder {
  id: number;
  person_id: number;
  order_type: string | null;
  filed_date: string | null;
  granted_date: string | null;
  expiration_date: string | null;
  status: string | null;
  details: string | null;
  client_first_name?: string | null;
  client_last_name?: string | null;
  client_internal_id?: string | null;
}

interface AllCourtAdvocacy {
  id: number;
  person_id: number;
  hearing_date: string | null;
  hearing_type: string | null;
  disposition: string | null;
  charges: string | null;
  notes: string | null;
  court_name?: string | null;
  judge_name?: string | null;
  client_first_name?: string | null;
  client_last_name?: string | null;
  client_internal_id?: string | null;
  staff_first_name?: string | null;
  staff_last_name?: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

type TabId = "dashboard" | "cases" | "orders" | "advocacy";

const tabs: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "cases", label: "Legal Cases", icon: FileText },
  { id: "orders", label: "Protection Orders", icon: Shield },
  { id: "advocacy", label: "Court Advocacy", icon: Gavel },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clientName(row: { client_first_name?: string | null; client_last_name?: string | null }): string {
  const first = row.client_first_name || "";
  const last = row.client_last_name || "";
  return (first + " " + last).trim() || "Unknown";
}

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  granted: "bg-green-100 text-green-800",
  denied: "bg-red-100 text-red-800",
  expired: "bg-gray-100 text-gray-800",
  violated: "bg-red-100 text-red-800",
  renewed: "bg-blue-100 text-blue-800",
  dismissed: "bg-gray-100 text-gray-800",
};

function renderCaseDetailsSummary(raw: string | null): string {
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) {
      const entries = Object.entries(parsed).filter(([, v]) => v);
      return entries
        .slice(0, 3)
        .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
        .join(" | ");
    }
  } catch { /* plain text */ }
  return raw.length > 100 ? raw.slice(0, 100) + "..." : raw;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LegalPage() {
  const [activeTab, setActiveTab] = useState<TabId>("dashboard");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Legal / Court Advocacy
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage legal cases, protection orders, and court advocacy
          </p>
        </div>
        <Link to="/legal/courts">
          <Button variant="outline" className="gap-2">
            <Building2 className="h-4 w-4" />
            Manage Courts & Judges
          </Button>
        </Link>
      </div>

      {/* Tab Navigation */}
      <div className="rounded-xl border bg-muted/30 p-2">
        <nav className="flex flex-wrap gap-1.5">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all",
                activeTab === tab.id
                  ? "bg-background text-primary shadow-sm ring-1 ring-border"
                  : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
              )}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "dashboard" && <DashboardTab />}
      {activeTab === "cases" && <AllCasesTab />}
      {activeTab === "orders" && <AllOrdersTab />}
      {activeTab === "advocacy" && <AllAdvocacyTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Tab (existing content)
// ---------------------------------------------------------------------------

function DashboardTab() {
  const { data: summaryData, isLoading: summaryLoading } = useQuery<{
    data: LegalSummary;
  }>({
    queryKey: ["legalSummary"],
    queryFn: () => api.get<{ data: LegalSummary }>("/legal/summary"),
  });

  const { data: expiringData } = useQuery<{ data: ExpiringOrder[] }>({
    queryKey: ["expiringOrders"],
    queryFn: () =>
      api.get<{ data: ExpiringOrder[] }>("/legal/expiring-orders", {
        params: { days: "30" },
      }),
  });

  const summary = summaryData?.data;
  const expiringOrders = expiringData?.data ?? [];

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  "flex h-12 w-12 items-center justify-center rounded-lg",
                  (summary?.expiringOrdersCount ?? 0) > 0
                    ? "bg-red-100"
                    : "bg-green-100",
                )}
              >
                <AlertTriangle
                  className={cn(
                    "h-6 w-6",
                    (summary?.expiringOrdersCount ?? 0) > 0
                      ? "text-red-600"
                      : "text-green-600",
                  )}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Expiring Orders
                </p>
                <p className="text-2xl font-bold">
                  {summaryLoading ? "-" : summary?.expiringOrdersCount ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">Next 30 days</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Upcoming Hearings
                </p>
                <p className="text-2xl font-bold">
                  {summaryLoading ? "-" : summary?.upcomingHearingsCount ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">Next 30 days</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Legal Cases
                </p>
                <p className="text-2xl font-bold">
                  {summaryLoading ? "-" : summary?.openCasesCount ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Expiring Orders */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-5 w-5" />
              Expiring Protection Orders
            </CardTitle>
            <CardDescription>
              Orders expiring within the next 30 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            {expiringOrders.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No protection orders expiring soon.
              </p>
            ) : (
              <div className="space-y-2">
                {expiringOrders.map((order) => {
                  const daysUntil = order.expiration_date
                    ? Math.ceil(
                        (new Date(order.expiration_date).getTime() -
                          Date.now()) /
                          (1000 * 60 * 60 * 24),
                      )
                    : null;

                  return (
                    <div
                      key={order.id}
                      className={cn(
                        "flex items-center justify-between rounded-md border px-3 py-2",
                        daysUntil !== null && daysUntil <= 7
                          ? "border-red-300 bg-red-50"
                          : "border-amber-200 bg-amber-50",
                      )}
                    >
                      <div>
                        <p className="text-sm font-medium capitalize">
                          {(order.order_type ?? "protection order").replace(
                            /_/g,
                            " ",
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Person #{order.person_id}
                          {order.expiration_date &&
                            ` | Expires: ${new Date(order.expiration_date).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="text-right">
                        {daysUntil !== null && (
                          <span
                            className={cn(
                              "text-xs font-medium",
                              daysUntil <= 7
                                ? "text-red-600"
                                : "text-amber-600",
                            )}
                          >
                            {daysUntil < 0
                              ? "Expired"
                              : `${daysUntil} day${daysUntil === 1 ? "" : "s"}`}
                          </span>
                        )}
                        <Link to={`/clients/${order.person_id}`}>
                          <Button variant="ghost" size="sm" className="ml-2">
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Gavel className="h-5 w-5" />
              Recent Court Advocacy
            </CardTitle>
            <CardDescription>Latest advocacy records</CardDescription>
          </CardHeader>
          <CardContent>
            {!summary?.recentActivity ||
            summary.recentActivity.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No recent court advocacy records.
              </p>
            ) : (
              <div className="space-y-2">
                {summary.recentActivity.map((activity) => {
                  const staffName =
                    activity.staff_first_name && activity.staff_last_name
                      ? `${activity.staff_first_name} ${activity.staff_last_name}`
                      : null;

                  return (
                    <div
                      key={activity.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium capitalize">
                          {(activity.hearing_type ?? "hearing").replace(
                            /_/g,
                            " ",
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {activity.hearing_date
                            ? new Date(
                                activity.hearing_date,
                              ).toLocaleDateString()
                            : new Date(
                                activity.created_at,
                              ).toLocaleDateString()}
                          {activity.court_name && ` | ${activity.court_name}`}
                          {staffName && ` | ${staffName}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {activity.disposition && (
                          <span className="text-xs text-muted-foreground">
                            {activity.disposition}
                          </span>
                        )}
                        <Link to={`/clients/${activity.person_id}`}>
                          <Button variant="ghost" size="sm">
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// All Legal Cases Tab
// ---------------------------------------------------------------------------

function AllCasesTab() {
  const { data, isLoading } = useQuery<{ data: AllLegalCase[] }>({
    queryKey: ["allLegalCases"],
    queryFn: () => api.get<{ data: AllLegalCase[] }>("/legal/all-cases"),
  });

  const cases = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          All legal cases across all clients. Click a client name or the edit button to manage.
        </p>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && cases.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No legal cases found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && cases.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Client</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Case Details</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Law Enforcement</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <Link
                        to={`/clients/${c.person_id}`}
                        className="flex items-center gap-1.5 text-primary hover:underline"
                      >
                        <User className="h-3.5 w-3.5" />
                        {clientName(c)}
                      </Link>
                      {c.client_internal_id && (
                        <p className="text-xs text-muted-foreground">{c.client_internal_id}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="truncate text-sm">
                        {renderCaseDetailsSummary(c.case_details)}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {c.law_enforcement || "-"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/clients/${c.person_id}`}>
                        <Button variant="ghost" size="icon" title="Edit in client view">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// All Protection Orders Tab
// ---------------------------------------------------------------------------

function AllOrdersTab() {
  const { data, isLoading } = useQuery<{ data: AllProtectionOrder[] }>({
    queryKey: ["allProtectionOrders"],
    queryFn: () => api.get<{ data: AllProtectionOrder[] }>("/legal/all-orders"),
  });

  const orders = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          All protection orders across all clients. Click a client name or the edit button to manage.
        </p>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && orders.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No protection orders found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && orders.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Client</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Filed</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Granted</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Expires</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Details</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const expWarning = order.expiration_date
                    ? (() => {
                        const days = Math.ceil(
                          (new Date(order.expiration_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
                        );
                        if (days < 0) return "expired";
                        if (days <= 7) return "red";
                        if (days <= 30) return "yellow";
                        return "none";
                      })()
                    : "none";

                  return (
                    <tr
                      key={order.id}
                      className={cn(
                        "border-b last:border-0",
                        expWarning === "red" && "bg-red-50",
                        expWarning === "expired" && "bg-red-50/50",
                        expWarning === "yellow" && "bg-amber-50",
                      )}
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/clients/${order.person_id}`}
                          className="flex items-center gap-1.5 text-primary hover:underline"
                        >
                          <User className="h-3.5 w-3.5" />
                          {clientName(order)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 capitalize">
                        {(order.order_type ?? "-").replace(/_/g, " ")}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                            statusColors[order.status ?? "pending"] ?? statusColors.pending,
                          )}
                        >
                          {order.status ?? "pending"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {order.filed_date ? new Date(order.filed_date).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {order.granted_date ? new Date(order.granted_date).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {order.expiration_date ? (
                          <span
                            className={cn(
                              "text-sm",
                              expWarning === "red" || expWarning === "expired"
                                ? "font-medium text-red-600"
                                : expWarning === "yellow"
                                  ? "font-medium text-amber-600"
                                  : "text-muted-foreground",
                            )}
                          >
                            {new Date(order.expiration_date).toLocaleDateString()}
                            {expWarning === "expired" && " (Expired)"}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <p className="truncate text-xs text-muted-foreground">
                          {order.details || "-"}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/clients/${order.person_id}`}>
                          <Button variant="ghost" size="icon" title="Edit in client view">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// All Court Advocacy Tab
// ---------------------------------------------------------------------------

function AllAdvocacyTab() {
  const { data, isLoading } = useQuery<{ data: AllCourtAdvocacy[] }>({
    queryKey: ["allCourtAdvocacy"],
    queryFn: () => api.get<{ data: AllCourtAdvocacy[] }>("/legal/all-advocacy"),
  });

  const records = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          All court advocacy records across all clients. Click a client name or the edit button to manage.
        </p>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && records.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Gavel className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No court advocacy records found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && records.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Client</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Court</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Judge</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Charges</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Disposition</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Staff</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const staffName =
                    r.staff_first_name && r.staff_last_name
                      ? `${r.staff_first_name} ${r.staff_last_name}`
                      : "-";

                  return (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <Link
                          to={`/clients/${r.person_id}`}
                          className="flex items-center gap-1.5 text-primary hover:underline"
                        >
                          <User className="h-3.5 w-3.5" />
                          {clientName(r)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {r.hearing_date
                          ? new Date(r.hearing_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-4 py-3">
                        {r.hearing_type ? (
                          <span className="inline-flex rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground capitalize">
                            {r.hearing_type.replace(/_/g, " ")}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-3">{r.court_name ?? "-"}</td>
                      <td className="px-4 py-3">{r.judge_name ?? "-"}</td>
                      <td className="px-4 py-3 max-w-[150px]">
                        <p className="truncate">{r.charges ?? "-"}</p>
                      </td>
                      <td className="px-4 py-3">{r.disposition ?? "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {staffName}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/clients/${r.person_id}`}>
                          <Button variant="ghost" size="icon" title="Edit in client view">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
